import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Plus, Minus, RotateCcw } from "lucide-react";

const MIN = 1;
const MAX = 6;
const clamp = (s) => Math.min(MAX, Math.max(MIN, s));

export const PlanLightbox = ({ src, label, onClose }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const areaRef = useRef(null);
  const dragging = useRef(false);
  const lastPoint = useRef({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const pinch = useRef({ dist: 0, cx: 0, cy: 0 });

  const reset = useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  // Zoom keeping the given viewport point (clientX/clientY) stable
  const zoomAt = useCallback((factor, clientX, clientY) => {
    const el = areaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx0 = rect.left + rect.width / 2;
    const cy0 = rect.top + rect.height / 2;
    const px = clientX - cx0;
    const py = clientY - cy0;
    setScale((prev) => {
      const next = clamp(prev * factor);
      const ratio = next / prev;
      setPos((p) => {
        if (next === MIN) return { x: 0, y: 0 };
        return { x: px - (px - p.x) * ratio, y: py - (py - p.y) * ratio };
      });
      return next;
    });
  }, []);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    zoomAt(e.deltaY < 0 ? 1.15 : 0.87, e.clientX, e.clientY);
  }, [zoomAt]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      dragging.current = true;
      lastPoint.current = { x: e.clientX, y: e.clientY };
    } else if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      pinch.current.dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const midX = (pts[0].x + pts[1].x) / 2;
      const midY = (pts[0].y + pts[1].y) / 2;
      if (pinch.current.dist > 0) {
        zoomAt(dist / pinch.current.dist, midX, midY);
      }
      pinch.current.dist = dist;
    } else if (dragging.current && scale > 1) {
      const dx = e.clientX - lastPoint.current.x;
      const dy = e.clientY - lastPoint.current.y;
      lastPoint.current = { x: e.clientX, y: e.clientY };
      setPos((p) => ({ x: p.x + dx, y: p.y + dy }));
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current.dist = 0;
    if (pointers.current.size === 0) dragging.current = false;
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") zoomAt(1.2, window.innerWidth / 2, window.innerHeight / 2);
      if (e.key === "-") zoomAt(0.83, window.innerWidth / 2, window.innerHeight / 2);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, zoomAt]);

  // Attach non-passive wheel listener so preventDefault works
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  return (
    <div
      data-testid="plan-lightbox"
      className="fixed inset-0 z-[100] bg-espresso/95 backdrop-blur-sm flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 sm:px-8 py-5 z-10">
        <span className="text-linen font-display text-lg sm:text-xl font-semibold truncate pr-4">{label}</span>
        <button
          data-testid="plan-lightbox-close"
          onClick={onClose}
          aria-label="Cerrar"
          className="w-11 h-11 rounded-full bg-linen/10 hover:bg-linen/20 text-linen flex items-center justify-center transition-colors shrink-0"
        >
          <X size={22} />
        </button>
      </div>

      {/* Image area */}
      <div
        ref={areaRef}
        className="w-full h-full flex items-center justify-center overflow-hidden touch-none"
        style={{ touchAction: "none", cursor: scale > 1 ? "grab" : "auto" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={(e) => (scale > 1 ? reset() : zoomAt(2, e.clientX, e.clientY))}
      >
        <img
          src={src}
          alt={label}
          draggable={false}
          data-testid="plan-lightbox-image"
          className="max-w-[92vw] max-h-[82vh] object-contain select-none"
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            transition: dragging.current ? "none" : "transform 0.12s ease-out",
            willChange: "transform",
          }}
        />
      </div>

      {/* Controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-linen/95 rounded-full px-3 py-2 shadow-xl z-10">
        <button
          data-testid="plan-zoom-out"
          onClick={() => zoomAt(0.83, window.innerWidth / 2, window.innerHeight / 2)}
          aria-label="Alejar"
          className="w-10 h-10 rounded-full text-espresso hover:bg-camel/25 flex items-center justify-center transition-colors"
        >
          <Minus size={20} />
        </button>
        <span className="text-espresso text-sm font-bold w-14 text-center tabular-nums">{Math.round(scale * 100)}%</span>
        <button
          data-testid="plan-zoom-in"
          onClick={() => zoomAt(1.2, window.innerWidth / 2, window.innerHeight / 2)}
          aria-label="Acercar"
          className="w-10 h-10 rounded-full text-espresso hover:bg-camel/25 flex items-center justify-center transition-colors"
        >
          <Plus size={20} />
        </button>
        <span className="w-px h-6 bg-camel/40 mx-1" />
        <button
          data-testid="plan-zoom-reset"
          onClick={reset}
          aria-label="Restablecer"
          className="w-10 h-10 rounded-full text-espresso hover:bg-camel/25 flex items-center justify-center transition-colors"
        >
          <RotateCcw size={18} />
        </button>
      </div>
    </div>
  );
};
